import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API, getToken, SERVICE_STATUS_LABELS, SERVICE_STATUS_ORDER, SERVICE_STATUS_COLORS } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Download, FileDown, X } from "lucide-react";

function ServiceStatusPill({ status }) {
  const color = SERVICE_STATUS_COLORS[status] || "#0A0A0A";
  return (
    <span
      className="status-pill"
      style={{ borderColor: color, color }}
      data-testid={`service-status-${status}`}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
      {SERVICE_STATUS_LABELS[status] || status}
    </span>
  );
}

export default function ServicesPage({ historyMode = false } = {}) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [driverFilter, setDriverFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const CLOSED_STATUSES = ["finalizado", "no_show", "cancelado"];
  const visibleStatuses = historyMode
    ? CLOSED_STATUSES
    : SERVICE_STATUS_ORDER.filter((s) => !CLOSED_STATUSES.includes(s));

  const buildParams = () => {
    const p = {};
    if (statusFilter !== "all") p.status = statusFilter;
    else p.closed = historyMode ? "only" : "exclude";
    if (driverFilter !== "all") p.driver_id = driverFilter;
    if (q.trim()) p.q = q.trim();
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  };

  const needsFilter = historyMode && !q.trim() && !dateFrom && !dateTo;

  const load = async () => {
    setLoading(true);
    try {
      const [list, st, drv] = await Promise.all([
        needsFilter ? Promise.resolve({ data: [] }) : api.get("/services", { params: buildParams() }),
        api.get("/services/stats/summary"),
        api.get("/drivers"),
      ]);
      setItems(list.data);
      setStats(st.data);
      setDrivers(drv.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => load(), 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, driverFilter, dateFrom, dateTo]);

  const exportPDF = () => {
    const params = new URLSearchParams(Object.entries(buildParams()).map(([k, v]) => [k, String(v)]));
    params.append("auth", getToken());
    window.open(`${API}/services/export.pdf?${params.toString()}`, "_blank");
  };

  const totals = stats || { total: 0, today: 0, by_status: {} };
  const hasFilters = q || statusFilter !== "all" || driverFilter !== "all" || dateFrom || dateTo;

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-[1500px] mx-auto" data-testid={historyMode ? "services-history-page" : "services-page"}>
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
            Centro de Control
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            {historyMode ? "Historial de servicios" : "Reservas"}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={exportPDF}
            variant="outline"
            className="rounded-none border-black h-11 px-4 text-xs uppercase tracking-[0.2em]"
            data-testid="services-export-pdf"
          >
            <FileDown size={16} className="mr-2" /> PDF
          </Button>
          {!historyMode && (
            <Button
              onClick={() => navigate("/services/new")}
              className="rounded-none btn-primary h-11 px-5 text-xs uppercase tracking-[0.2em]"
              data-testid="new-service-cta"
            >
              <Plus size={16} className="mr-2" /> Nueva reserva
            </Button>
          )}
        </div>
      </header>

      {!historyMode && (
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-black mb-8">
        {[
          { key: "total", label: "Total reservas", value: totals.total },
          { key: "today", label: "Hoy", value: totals.today },
          { key: "asignado", label: "Asignadas", value: totals.by_status?.asignado || 0 },
          {
            key: "pendiente",
            label: "Pendientes de asignar",
            value: totals.by_status?.pendiente_asignacion || 0,
          },
        ].map((m, i, a) => (
          <div
            key={m.key}
            className={`p-6 bg-white ${i < a.length - 1 ? "border-r border-black" : ""}`}
            data-testid={`service-metric-${m.key}`}
          >
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
              {m.label}
            </div>
            <div className="font-display text-5xl font-bold">{m.value}</div>
          </div>
        ))}
      </section>
      )}

      <section className="border border-black bg-white">
        <div className="border-b border-black p-4 flex flex-col gap-3">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="flex gap-2 flex-1 max-w-xl">
              <div className="relative flex-1">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
                />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                  placeholder="Buscar por reserva, cliente o teléfono…"
                  className="rounded-none border-black pl-9 h-10"
                  data-testid="services-search"
                />
              </div>
              <Button
                type="button"
                onClick={load}
                className="rounded-none btn-primary h-10 px-4 text-xs uppercase tracking-widest"
              >
                Buscar
              </Button>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger
                  className="rounded-none border-black h-10 w-[210px] text-xs uppercase tracking-widest"
                  data-testid="services-status-filter"
                >
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">{historyMode ? "Todos (finalizados)" : "Activos (todos)"}</SelectItem>
                  {visibleStatuses.map((s) => (
                    <SelectItem key={s} value={s}>
                      {SERVICE_STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={driverFilter} onValueChange={setDriverFilter}>
                <SelectTrigger
                  className="rounded-none border-black h-10 w-[210px] text-xs uppercase tracking-widest"
                  data-testid="services-driver-filter"
                >
                  <SelectValue placeholder="Conductor" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">Todos los conductores</SelectItem>
                  {drivers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3 pt-3 border-t border-zinc-200">
            <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 shrink-0">
              Rango de fechas (recogida)
            </span>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-none border-black h-10 w-[170px]"
                data-testid="services-date-from"
              />
              <span className="text-zinc-400 text-xs">→</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-none border-black h-10 w-[170px]"
                data-testid="services-date-to"
              />
            </div>
            {hasFilters && (
              <button
                type="button"
                onClick={() => {
                  setQ("");
                  setStatusFilter("all");
                  setDriverFilter("all");
                  setDateFrom("");
                  setDateTo("");
                }}
                className="ml-auto text-[11px] uppercase tracking-widest text-zinc-600 hover:text-black inline-flex items-center gap-1"
                data-testid="services-clear-filters"
              >
                <X size={12} /> Limpiar filtros
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="ds-table w-full" data-testid="services-table">
            <thead>
              <tr>
                <th>Reserva</th>
                <th>Fecha / Hora</th>
                <th>Cliente</th>
                <th>Precio</th>
                <th>Conductor</th>
                <th>Pago</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center text-zinc-500 py-12">
                    Cargando…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-zinc-500 py-16">
                    {needsFilter
                      ? "Selecciona un rango de fechas o busca un servicio para ver el historial."
                      : "Sin reservas para estos filtros."}
                  </td>
                </tr>
              ) : (
                items.map((s) => (
                  <tr key={s.id} data-testid={`service-row-${s.booking_number}`}>
                    <td className="font-mono font-semibold">
                      {s.booking_number}
                      {s.provider && s.provider !== "ds_travel_tours" && (
                        <div
                          className="mt-1 inline-block text-[9px] font-bold uppercase tracking-widest bg-[#F5B301] text-black px-1.5 py-0.5"
                          data-testid={`provider-tag-${s.booking_number}`}
                        >
                          {s.provider === "booking" ? "Booking" : s.provider === "jayride" ? "Jayride" : s.provider === "viator" ? "Viator" : s.provider}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      {s.pickup_date}
                      <div className="text-xs text-zinc-500">{s.pickup_time}</div>
                    </td>
                    <td>
                      {s.client_name}
                      <div className="text-xs text-zinc-500">{s.client_phone}</div>
                    </td>
                    <td className="font-semibold whitespace-nowrap" data-testid={`service-price-${s.booking_number}`}>
                      {s.price == null ? <span className="text-zinc-400">—</span> : `${Number(s.price).toFixed(2)} €`}
                    </td>
                    <td>{s.driver?.name || <span className="text-zinc-400">—</span>}</td>
                    <td>
                      {s.provider && s.provider !== "ds_travel_tours" ? (
                        <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-500 text-zinc-500">
                          Externo
                        </span>
                      ) : s.price == null ? (
                        <span className="text-zinc-400">—</span>
                      ) : s.payment_status === "paid" ? (
                        <span
                          className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-emerald-700 text-emerald-700"
                          data-testid={`payment-paid-${s.booking_number}`}
                        >
                          Pagado
                        </span>
                      ) : (
                        <span
                          className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-[#E63946] text-[#E63946]"
                          data-testid={`payment-unpaid-${s.booking_number}`}
                        >
                          Pendiente
                        </span>
                      )}
                    </td>
                    <td>
                      <ServiceStatusPill status={s.status} />
                    </td>
                    <td className="text-right">
                      <Link
                        to={`/services/${s.id}`}
                        className="text-xs uppercase tracking-widest font-semibold underline underline-offset-4 hover:text-zinc-500"
                        data-testid={`open-service-${s.booking_number}`}
                      >
                        Abrir →
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
